import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input } from "reactstrap";
import Select from "react-select";
import { useTable } from "react-table";
import axiosInstance from "src/common/axiosInstance";
import { toast } from "react-toastify";
import Pagination from "src/components/Common/Pagination";

interface MarginEntry {
  exchange: number;
  margin: number;
  exchange_name?: string;
  icon_url?: string;
}

interface ExchangeOption {
  value: number | string;
  label: string;
  icon_url?: string;
}

interface Order {
  Order_Name__c: string;
  Status: string;
}

interface Member {
  name: string;
  email: string;
  permission_type: string;
  status: string;
}

interface ClientMarginModalProps {
  isOpen: boolean;
  toggle: () => void;
  client: { client_id: number | string; client_name: string } | null;
  readOnly?: boolean;
}

const PAGE_SIZE = 5;

const ORDER_STATUS_MAP: Record<string, { label: string; badge: string }> = {
  active:       { label: "Active",    badge: "margin-status-active" },
  pending:      { label: "Pending",   badge: "margin-status-pending" },
  unassigned:   { label: "Pending",   badge: "margin-status-pending" },
  atrisk:       { label: "Pending",   badge: "margin-status-pending" },
  proposal:     { label: "Pending",   badge: "margin-status-pending" },
  "late-start": { label: "Pending",   badge: "margin-status-pending" },
  ended:        { label: "Finished",  badge: "margin-status-completed" },
  finished:     { label: "Finished",  badge: "margin-status-completed" },
  paused:       { label: "Paused",    badge: "margin-status-completed" },
  cancelled:    { label: "Cancelled", badge: "margin-status-completed" },
};

const ClientMarginModal: React.FC<ClientMarginModalProps> = ({
  isOpen,
  toggle,
  client,
  readOnly = false,
}) => {
  const [margins, setMargins] = useState<MarginEntry[]>([]);
  const [savedMargins, setSavedMargins] = useState<MarginEntry[]>([]);
  const [exchangeOptions, setExchangeOptions] = useState<ExchangeOption[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTabId, setActiveTabId] = useState("orders");
  const [selectedExchange, setSelectedExchange] = useState<ExchangeOption | null>(null);
  const [marginValue, setMarginValue] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(PAGE_SIZE);
  const [membersPage, setMembersPage] = useState(1);
  const [membersPageSize, setMembersPageSize] = useState(PAGE_SIZE);

  useEffect(() => {
    if (isOpen && client) {
      setLoading(true);
      setActiveTabId("orders");
      setSelectedExchange(null);
      setMarginValue("");
      setSearchFilter("");
      setOrdersPage(1);
      setMembersPage(1);

      Promise.all([
        axiosInstance.get(`/margins?client_id=${client.client_id}`),
        axiosInstance.get(`/margin-exchanges`),
        axiosInstance.get(`/client-details?client_id=${client.client_id}`),
      ])
        .then(([marginsRes, exchangeRes, detailsRes]) => {
          const loaded = marginsRes.data.data || [];
          setMargins(loaded);
          setSavedMargins(loaded);
          setExchangeOptions(
            (exchangeRes.data.data || []).map((e: any) => ({
              value: e.value,
              label: e.label,
              icon_url: e.icon_url,
            }))
          );
          setOrders(detailsRes.data.data?.orders || []);
          setMembers(detailsRes.data.data?.members || []);
        })
        .catch(() => {
          toast.error("Failed to load data");
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, client]);

  const availableExchanges = useMemo(() => {
    const addedIds = margins.map((m) => m.exchange);
    const filtered = exchangeOptions.filter(
      (e) => !addedIds.includes(Number(e.value))
    );
    // return [{ value: "all", label: "All Exchanges" }, ...filtered];
    return filtered;
  }, [exchangeOptions, margins]);

  const handleAdd = () => {
    const val = parseFloat(marginValue);
    if (!selectedExchange) {
      toast.error("Please select an exchange");
      return;
    }
    if (isNaN(val) || val <= 0 || val > 100) {
      toast.error("Margin must be greater than 0 and at most 100");
      return;
    }

    // if (selectedExchange.value === "all") {
    //   const addedIds = margins.map((m) => m.exchange);
    //   const newMargins = exchangeOptions
    //     .filter((e) => !addedIds.includes(Number(e.value)))
    //     .map((e) => ({
    //       exchange: Number(e.value),
    //       margin: val,
    //       exchange_name: e.label,
    //       icon_url: e.icon_url,
    //     }));
    //   setMargins([...margins, ...newMargins]);
    // } else {
    setMargins([
      ...margins,
      {
        exchange: Number(selectedExchange.value),
        margin: val,
        exchange_name: selectedExchange.label,
        icon_url: selectedExchange.icon_url,
      },
    ]);
    // }

    setSelectedExchange(null);
    setMarginValue("");
  };

  const handleDelete = (exchangeId: number) => {
    setMargins(margins.filter((m) => m.exchange !== exchangeId));
  };

  const handleMarginChange = (exchangeId: number, newValue: string) => {
    const parsed = parseFloat(newValue);
    if (newValue === "" || (!isNaN(parsed) && parsed >= 0 && parsed <= 100)) {
      setMargins(
        margins.map((m) =>
          m.exchange === exchangeId
            ? { ...m, margin: newValue === "" ? 0 : parsed }
            : m
        )
      );
    }
  };

  const isDirty = useMemo(() => {
    if (margins.length !== savedMargins.length) return true;
    const sorted = [...margins].sort((a, b) => a.exchange - b.exchange);
    const savedSorted = [...savedMargins].sort((a, b) => a.exchange - b.exchange);
    return sorted.some(
      (m, i) => m.exchange !== savedSorted[i].exchange || m.margin !== savedSorted[i].margin
    );
  }, [margins, savedMargins]);

  const hasInvalidMargins = useMemo(
    () => margins.some((m) => !m.margin || m.margin <= 0),
    [margins]
  );

  const isSaveDisabled =
    !isDirty ||
    saving ||
    loading ||
    (!!selectedExchange && !marginValue) ||
    hasInvalidMargins;

  const handleSave = async () => {
    if (!client) return;
    if (selectedExchange && marginValue) {
      toast.error("You have not added a margin. Click the + button to add one first.");
      return;
    }
    if (hasInvalidMargins) {
      toast.error("Please add a margin before saving.");
      return;
    }
    setSaving(true);
    try {
      const payload = margins.map((m) => ({
        exchange: m.exchange,
        margin: m.margin,
      }));
      const res = await axiosInstance.post(`/margins`, {
        client_id: client.client_id,
        margins: payload,
      });
      const saved = res.data.data || [];
      setMargins(saved);
      setSavedMargins(saved);
      toast.success("Margins saved successfully");
      toggle();
    } catch {
      toast.error("Failed to save margins");
    } finally {
      setSaving(false);
    }
  };

  const filteredMargins = useMemo(() => {
    if (!searchFilter.trim()) return margins;
    const lower = searchFilter.toLowerCase();
    return margins.filter((m) =>
      (m.exchange_name || "").toLowerCase().includes(lower)
    );
  }, [margins, searchFilter]);

  const hasOrders = orders.length > 0;
  const hasMembers = members.length > 0;
  const hasTabs = hasOrders || hasMembers;

  // Set default active tab based on available data
  useEffect(() => {
    if (!loading && !hasOrders && hasMembers) {
      setActiveTabId("members");
    }
  }, [loading, hasOrders, hasMembers]);

  // Paginated data
  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ordersPageSize;
    return orders.slice(start, start + ordersPageSize);
  }, [orders, ordersPage, ordersPageSize]);

  const ordersTotalPages = Math.ceil(orders.length / ordersPageSize);

  const paginatedMembers = useMemo(() => {
    const start = (membersPage - 1) * membersPageSize;
    return members.slice(start, start + membersPageSize);
  }, [members, membersPage, membersPageSize]);

  const membersTotalPages = Math.ceil(members.length / membersPageSize);

  const orderColumns: any[] = useMemo(
    () => [
      {
        Header: "Campaign Name",
        accessor: "Order_Name__c",
      },
      {
        Header: "Status",
        accessor: "Status",
        Cell: ({ value }: { value: string }) => {
          const mapped = ORDER_STATUS_MAP[(value || "").toLowerCase()]
            ?? { label: value, badge: "margin-status-completed" };
          return <span className={`margin-status-badge ${mapped.badge}`}>{mapped.label}</span>;
        },
      },
    ],
    []
  );

  const memberColumns: any[] = useMemo(
    () => [
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Permission Type", accessor: "permission_type" },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }: { value: string }) => {
          const isActive = (value || "").toLowerCase() === "active";
          return (
            <span className={`margin-status-badge ${isActive ? "margin-status-active" : "margin-status-pending"}`}>
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  const orderTable = useTable({ columns: orderColumns, data: paginatedOrders });
  const memberTable = useTable({ columns: memberColumns, data: paginatedMembers });

  const renderTable = (tableInstance: any) => {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
      tableInstance;
    return (
      <table className="table table-bordered" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup: any) => (
            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <th key={column.id} {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length > 0 ? (
            rows.map((row: any) => {
              prepareRow(row);
              return (
                <tr key={row.getRowProps().key} {...row.getRowProps()}>
                  {row.cells.map((cell: any) => (
                    <td key={cell.id} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={tableInstance.columns.length}
                className="text-center text-muted py-3"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const selectStyles = {
    input: (provided: any) => ({
      ...provided,
      fontSize: "16px",
    }),
    singleValue: (base: any) => ({
      ...base,
      whiteSpace: "nowrap" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "calc(100% - 8px)",
    }),
    option: (base: any) => ({
      ...base,
      whiteSpace: "nowrap" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      backgroundColor: "transparent",
      color: "inherit",
      ":active": { backgroundColor: "transparent" },
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 1000,
      borderRadius: "4px",
    }),
    menuList: (base: any) => ({
      ...base,
      paddingTop: "0px",
      paddingBottom: "0px",
      borderRadius: "4px",
    }),
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="general-custom-modal modal-top-mobile"
    >
      <div className="modal-header">
        <h5 className="modal-title mt-0">Client Margin Configuration</h5>
        <button
          onClick={toggle}
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body">
        {loading ? (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm"></span>
            <p className="mt-2 text-muted">Loading...</p>
          </div>
        ) : (
          <>
            {!readOnly && (
              <p className="text-muted mb-3">
                Configuring exchange margins for{" "}
                <strong>{client?.client_name}</strong>
              </p>
            )}

            {!readOnly && (
              <>
                <div className="d-flex align-items-center gap-2 mb-3 margin-add-row">
                  <div className="margin-exchange-select">
                    <Select
                      classNamePrefix="react-select"
                      value={selectedExchange}
                      onChange={(opt: any) => setSelectedExchange(opt)}
                      options={availableExchanges}
                      placeholder="Select Exchange"
                      isClearable
                      styles={selectStyles}
                    />
                  </div>
                  <Input
                    type="number"
                    placeholder="e.g. 15"
                    value={marginValue}
                    onChange={(e) => setMarginValue(e.target.value)}
                    className="margin-percent-input"
                    min={0}
                    max={100}
                  />
                  <button
                    type="button"
                    className="btn btn-primary button-primary btn-margin-add"
                    onClick={handleAdd}
                    disabled={!selectedExchange || !marginValue}
                    title="Add Margin"
                  >
                    <i className="uil uil-plus font-size-16"></i>
                  </button>
                </div>
                {selectedExchange && marginValue && (
                  <small className="form-text text-warning mb-2 d-block text-end">
                    Click the <strong>+</strong> button to add this margin.
                  </small>
                )}
              </>
            )}

            {/* Search filter + clear all above margin rows */}
            {margins.length > 1 && (!readOnly || margins.length > 4) && (
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                {margins.length > 4 && (
                  <Input
                    type="text"
                    placeholder="Search margins..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="flex-grow-1"
                  />
                )}
                {!readOnly && margins.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary button-secondary btn-margin-clear-all"
                    onClick={() => setMargins([])}
                    title="Clear All"
                  >
                    <i className="mdi mdi-delete-sweep font-size-16"></i>
                  </button>
                )}
              </div>
            )}

            {/* Margin rows */}
            {margins.length > 0 && (
              <div className="margin-rows-container">
                {filteredMargins.map((m) => (
                  <div key={m.exchange} className="margin-row">
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      {m.icon_url && (
                        <img
                          src={m.icon_url}
                          alt={m.exchange_name}
                          className="margin-exchange-icon"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <span className="margin-exchange-name">
                        {m.exchange_name}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {readOnly ? (
                        <span className="fw-medium">{m.margin}%</span>
                      ) : (
                        <>
                          <Input
                            type="number"
                            value={m.margin}
                            onChange={(e) =>
                              handleMarginChange(m.exchange, e.target.value)
                            }
                            className="margin-inline-input"
                            min={0}
                            max={100}
                          />
                          <span className="text-muted">%</span>
                          <button
                            className="btn-margin-delete"
                            onClick={() => handleDelete(m.exchange)}
                            title="Delete"
                          >
                            <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state when no margins, orders, or members */}
            {margins.length === 0 && !hasTabs && (
              <p className="text-center text-muted py-3">
                No members or orders associated with <strong>{client?.client_name}</strong>
              </p>
            )}

            {/* Tabs: Orders / Members — only show when data exists */}
            {hasTabs && (
              <>
                <ul className="nav nav-tabs margin-tab-bar mt-3">
                  {hasOrders && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTabId === "orders" ? "active" : ""}`}
                        onClick={() => setActiveTabId("orders")}
                      >
                        Orders ({orders.length})
                      </button>
                    </li>
                  )}
                  {hasMembers && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTabId === "members" ? "active" : ""}`}
                        onClick={() => setActiveTabId("members")}
                      >
                        Members ({members.length})
                      </button>
                    </li>
                  )}
                </ul>
                <div className="mt-3 margin-table-wrapper">
                  {activeTabId === "orders" && hasOrders && (
                    <>
                      {renderTable(orderTable)}
                      {ordersTotalPages > 1 && (
                        <Pagination
                          totalPages={ordersTotalPages}
                          currentPage={ordersPage}
                          currentCount={paginatedOrders.length}
                          onPageChange={setOrdersPage}
                          pageSize={ordersPageSize}
                          onPageSizeChange={(size) => {
                            setOrdersPageSize(size);
                            setOrdersPage(1);
                          }}
                          menuPlacement="top"
                        />
                      )}
                    </>
                  )}
                  {activeTabId === "members" && hasMembers && (
                    <>
                      {renderTable(memberTable)}
                      {membersTotalPages > 1 && (
                        <Pagination
                          totalPages={membersTotalPages}
                          currentPage={membersPage}
                          currentCount={paginatedMembers.length}
                          onPageChange={setMembersPage}
                          pageSize={membersPageSize}
                          onPageSizeChange={(size) => {
                            setMembersPageSize(size);
                            setMembersPage(1);
                          }}
                          menuPlacement="top"
                        />
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="modal-footer">
        {!readOnly && (
          <button
            className="btn btn-primary button-primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              "Save"
            )}
          </button>
        )}
        <button
          className="btn btn-danger button-secondary"
          onClick={toggle}
        >
          {readOnly ? "Close" : "Cancel"}
        </button>
      </div>
    </Modal>
  );
};

export default ClientMarginModal;
